import { Loader2, Inbox, AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Reusable UI state blocks: loading, empty, and error. Keeps state handling
 * consistent across every future module.
 */
export const LoadingState = ({ label = "Loading", className }) => (
  <div
    className={cn("flex items-center gap-3 text-sm text-muted-foreground", className)}
    role="status"
    aria-live="polite"
    data-testid="state-loading"
  >
    <Loader2 className="h-4 w-4 animate-spin text-accent" />
    <span>{label}…</span>
  </div>
);

export const SkeletonRows = ({ rows = 3 }) => (
  <div className="space-y-3" data-testid="state-skeleton">
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} className="h-12 w-full rounded-lg" />
    ))}
  </div>
);

export const EmptyState = ({ title = "Nothing here yet", description, action, className }) => (
  <div
    className={cn(
      "flex flex-col items-center rounded-xl border border-dashed border-border bg-secondary/40 px-6 py-12 text-center",
      className,
    )}
    data-testid="state-empty"
  >
    <div className="mb-4 grid h-11 w-11 place-items-center rounded-full bg-background text-muted-foreground shadow-card">
      <Inbox className="h-5 w-5" />
    </div>
    <p className="font-medium text-foreground">{title}</p>
    {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
    {action}
  </div>
);

export const ErrorState = ({ title = "Couldn't load this", description, onRetry, className }) => (
  <div
    className={cn(
      "flex flex-col items-center rounded-xl border border-destructive/25 bg-destructive/5 px-6 py-12 text-center",
      className,
    )}
    data-testid="state-error"
  >
    <div className="mb-4 grid h-11 w-11 place-items-center rounded-full bg-destructive/10 text-destructive">
      <AlertTriangle className="h-5 w-5" />
    </div>
    <p className="font-medium text-foreground">{title}</p>
    {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
    {onRetry && (
      <Button variant="outline" className="mt-5" onClick={onRetry} data-testid="state-error-retry">
        Retry
      </Button>
    )}
  </div>
);
